/**
 * Looking round while locked (user 10-11): with a target locked, holding the right mouse button turns the character to
 * face the way the camera looks (the shared rules: systems/lockLook.ts; the client only sends `lockOn` `look`). The
 * guard the press raised stays up (render/cameraTurn.ts `keepsGuard`); letting go faces the target again. No lock: no
 * effect. (The gamepad turns the camera with the right stick and has no held "look" button yet; touch has no right
 * button: neither sends a look.)
 */
import { duelLockOf, screenDirToWorld, type PlayerCommand, type World } from '@nine/shared';

export class LockLook {
  private sent: [number, number] | null = null;

  constructor(
    private readonly world: World,
    private readonly dirToMap: (sx: number, sy: number) => [number, number],
    private readonly send: (c: PlayerCommand) => void,
  ) {}

  /** Called when the right button goes down or up, the pointer drags, or the camera turns: `held` = right button down. */
  sync(held: boolean): void {
    const locked = duelLockOf(this.world)?.lockedOf(this.world.player.id) != null;
    if (!held || !locked) {
      if (this.sent) this.send({ t: 'lockOn', op: 'look', dx: 0, dy: 0 });
      this.sent = null;
      return;
    }
    // The camera's forward on the map: straight up the screen.
    const [dx, dy] = this.dirToMap(...screenDirToWorld(0, -1));
    if (this.sent && Math.abs(this.sent[0] - dx) < 1e-3 && Math.abs(this.sent[1] - dy) < 1e-3) return;
    this.sent = [dx, dy];
    this.send({ t: 'lockOn', op: 'look', dx, dy });
  }
}
